import JSZip from 'jszip';

/**
 * Minimal .docx reader for liturgy import. A .docx is a zip of WordprocessingML;
 * we pull `word/document.xml` (the body) and `word/footnotes.xml`, and reduce
 * each paragraph to what the semantic mapping needs: its style, whether it has a
 * bold / italic run, its text, and any footnote references. Deliberately not a
 * general docx converter — just the signal mapToDoc consumes.
 */

const W = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';

export interface DocxParagraph {
  style: string; // pStyle val ('' = Normal/body)
  bold: boolean; // any run bold
  italic: boolean; // any run italic
  text: string;
  footnoteIds: string[];
  // Google-Doc import only: the paragraph's text is red (the liturgy rubric
  // convention). .docx import leaves this unset — rubrics there are Subtitle-styled.
  rubricColor?: boolean;
  // Google-Doc import only: left indent in points. The mapper pairs it with bold
  // to identify Congregation responses (bold alone is ambiguous — headers are bold
  // too). .docx import leaves it unset, keeping that path on the bold-only rule.
  indentPt?: number;
}
export interface ParsedDocx {
  paragraphs: DocxParagraph[];
  footnotes: Map<string, string>; // footnote id → text
}

const els = (root: Element | Document, name: string): Element[] =>
  Array.from(root.getElementsByTagNameNS(W, name));

// A `w:b` / `w:i` toggle is on when present unless explicitly val="0"/"false".
const toggleOn = (rpr: Element, name: string): boolean => {
  const t = rpr.getElementsByTagNameNS(W, name)[0];
  if (!t) return false;
  const v = t.getAttributeNS(W, 'val');
  return v !== '0' && v !== 'false';
};

export async function parseDocx(file: File): Promise<ParsedDocx> {
  const zip = await JSZip.loadAsync(file);
  const docXml = await zip.file('word/document.xml')?.async('string');
  if (!docXml) throw new Error('That doesn’t look like a Word document (no document.xml).');

  const parser = new DOMParser();
  const dom = parser.parseFromString(docXml, 'application/xml');
  if (dom.getElementsByTagName('parsererror').length) {
    throw new Error('Could not read the document’s XML.');
  }

  const paragraphs: DocxParagraph[] = els(dom, 'p').map((p) => {
    const pStyle = p.getElementsByTagNameNS(W, 'pStyle')[0];
    const style = pStyle?.getAttributeNS(W, 'val') ?? '';
    let text = '';
    let bold = false;
    let italic = false;
    const footnoteIds: string[] = [];
    for (const run of els(p, 'r')) {
      const rpr = run.getElementsByTagNameNS(W, 'rPr')[0];
      if (rpr) {
        if (toggleOn(rpr, 'b')) bold = true;
        if (toggleOn(rpr, 'i')) italic = true;
      }
      for (const t of els(run, 't')) text += t.textContent ?? '';
      for (const fr of els(run, 'footnoteReference')) {
        const id = fr.getAttributeNS(W, 'id');
        if (id) footnoteIds.push(id);
      }
    }
    return { style, bold, italic, text, footnoteIds };
  });

  const footnotes = new Map<string, string>();
  const fnXml = await zip.file('word/footnotes.xml')?.async('string');
  if (fnXml) {
    const fdom = parser.parseFromString(fnXml, 'application/xml');
    for (const f of els(fdom, 'footnote')) {
      // Skip the separator / continuationSeparator pseudo-footnotes (they carry
      // a w:type); everything else is a real note.
      if (f.getAttributeNS(W, 'type')) continue;
      const id = f.getAttributeNS(W, 'id');
      if (!id) continue;
      const text = els(f, 't')
        .map((t) => t.textContent ?? '')
        .join('')
        .trim();
      if (text) footnotes.set(id, text);
    }
  }

  return { paragraphs, footnotes };
}
